import { useState, useRef } from 'react';

const MAX_UPLOAD_BYTES = 1024 * 1024;
const MAX_IMAGE_DIMENSION = 1000;
const UPLOAD_TIMEOUT_MS = 30000;

const readImageElement = (file) => new Promise((resolve, reject) => {
  const imageUrl = URL.createObjectURL(file);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(imageUrl);
    resolve(image);
  };

  image.onerror = () => {
    URL.revokeObjectURL(imageUrl);
    reject(new Error('File gambar tidak bisa diproses'));
  };

  image.src = imageUrl;
});

const canvasToBlob = (canvas, mimeType, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) {
      resolve(blob);
      return;
    }
    reject(new Error('Gagal membuat file foto terkompresi'));
  }, mimeType, quality);
});

const compressImageFile = async (file) => {
  if (!file.type.startsWith('image/')) {
    throw new Error('File yang dipilih harus berupa gambar');
  }

  if (file.size <= MAX_UPLOAD_BYTES) {
    return file;
  }

  const image = await readImageElement(file);
  const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(image.width, image.height));
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Browser tidak mendukung kompresi foto');
  }

  canvas.width = targetWidth;
  canvas.height = targetHeight;
  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  const outputType = file.type === 'image/png' ? 'image/jpeg' : (file.type || 'image/jpeg');
  let quality = outputType === 'image/jpeg' ? 0.82 : 0.86;
  let blob = await canvasToBlob(canvas, outputType, quality);

  while (blob.size > MAX_UPLOAD_BYTES && quality > 0.45) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, outputType, quality);
  }

  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new Error('Ukuran foto masih melebihi batas 1 MB setelah dikompres');
  }

  return new File([blob], file.name.replace(/\.[^.]+$/, outputType === 'image/jpeg' ? '.jpg' : '.webp'), {
    type: outputType,
    lastModified: Date.now(),
  });
};

const buildUploadErrorMessage = ({ error, responseStatus, responseData }) => {
  if (error?.name === 'AbortError') {
    return 'Upload foto melebihi batas waktu 30 detik. Coba lagi dengan koneksi lebih stabil.';
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'Koneksi internet terputus. Periksa jaringan lalu coba lagi.';
  }

  if (typeof responseStatus === 'number' && responseStatus >= 500) {
    return 'Server upload sedang bermasalah. Coba beberapa saat lagi.';
  }

  if (typeof responseStatus === 'number' && responseStatus >= 400) {
    return 'Upload foto ditolak server: ' + (responseData?.message || responseData?.error || 'Periksa kembali file yang dipilih.');
  }

  if (responseData && responseData.status === false) {
    return 'Upload foto gagal: ' + (responseData.message || responseData.error || 'Terjadi kesalahan saat memproses file.');
  }

  if (error instanceof TypeError) {
    return 'Gagal terhubung ke server upload. Coba lagi beberapa saat lagi.';
  }

  return 'Upload foto gagal: ' + (error?.message || 'Terjadi kesalahan');
};

/**
 * InputModal — dipakai untuk Add dan Edit detail laporan.
 *
 * Props:
 *   uniforms      – array uniform untuk select
 *   onSave        – ({ uniform_id, qty, url_photo, notes }) => void
 *   onClose       – () => void
 *   editMode      – boolean (default false) → disable uniform select, prefill values
 *   initialValues – { uniform_id, qty, url_photo, notes } — dipakai saat editMode=true
 */
const InputModal = ({ uniforms = [], onSave, onClose, editMode = false, initialValues = null }) => {
  const [uniformId, setUniformId] = useState(initialValues?.uniform_id?.toString() || '');
  const [qty, setQty] = useState(initialValues?.qty?.toString() || '');
  // photoPreview: data URL untuk display (dari file picker)
  // photoUrl: data URL yang akan disimpan ke database (base64, permanen)
  const [photoPreview, setPhotoPreview] = useState(initialValues?.url_photo || null);
  const [photoUrl, setPhotoUrl] = useState(initialValues?.url_photo || null);
  const [notes, setNotes] = useState(initialValues?.notes || '');
  const [errors, setErrors] = useState({});
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState('');
  const fileRef = useRef();
  const retryFileRef = useRef(null);
  const canRetryUpload = Boolean(retryFileRef.current && uploadError && !uploading);

  const openFilePicker = () => {
    if (fileRef.current && !uploading && !saving) {
      fileRef.current.click();
    }
  };

  const handleModalClose = () => {
    if (saving || uploading) return;
    onClose();
  };

  const uploadPhoto = async (file) => {
    if (!file || uploading || saving) return;

    const hadExistingPhoto = Boolean(photoPreview || photoUrl);
    retryFileRef.current = file;
    setUploadError('');
    setUploadSuccess('');
    if (!hadExistingPhoto) {
      setPhotoPreview(null);
      setPhotoUrl(null);
    }
    setUploading(true);
    setUploadStage('Menyiapkan foto...');
    let timeoutId;
    let responseStatus;
    let responseData;
    try {
      setUploadStage('Mengompres foto...');
      const uploadFile = await compressImageFile(file);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('email', process.env.REACT_APP_CDN_EMAIL);
      formData.append('password', process.env.REACT_APP_CDN_PASSWORD);
      formData.append('user_email', process.env.REACT_APP_CDN_EMAIL);

      const controller = new AbortController();
      timeoutId = window.setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

      setUploadStage('Mengupload foto...');
      const res = await fetch(process.env.REACT_APP_CDN_URL, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      responseStatus = res.status;
      responseData = await res.json();

      if (!res.ok) {
        throw new Error('Upload ditolak server');
      }

      if (responseData.status && responseData.url) {
        retryFileRef.current = null;
        setUploadError('');
        setUploadSuccess(editMode
          ? 'Foto baru berhasil diupload. Tekan Simpan Perubahan untuk menyimpan update.'
          : 'Foto berhasil diupload dan siap disimpan.');
        setPhotoPreview(responseData.url);
        setPhotoUrl(responseData.url);
        setErrors((p) => ({ ...p, photo: undefined }));
      } else {
        if (!hadExistingPhoto) {
          setPhotoPreview(null);
          setPhotoUrl(null);
        }
        if (fileRef.current) fileRef.current.value = '';
        setUploadError(buildUploadErrorMessage({ responseStatus, responseData }));
      }
    } catch (err) {
      if (!hadExistingPhoto) {
        setPhotoPreview(null);
        setPhotoUrl(null);
      }
      if (fileRef.current) fileRef.current.value = '';
      setUploadError(buildUploadErrorMessage({ error: err, responseStatus, responseData }));
    } finally {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      setUploading(false);
      setUploadStage('');
    }
  };

  const handlePhotoChange = async (e) => {
    if (uploading || saving) {
      e.target.value = '';
      return;
    }

    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    await uploadPhoto(file);
  };

  const handleRetryUpload = async () => {
    if (!retryFileRef.current || uploading || saving) return;
    await uploadPhoto(retryFileRef.current);
  };

  const handleRemovePhoto = () => {
    setUploadError('');
    setUploadSuccess('');
    setPhotoPreview(null);
    setPhotoUrl(null);
    retryFileRef.current = null;
    if (fileRef.current) fileRef.current.value = '';
  };

  const validate = () => {
    const e = {};
    if (!uniformId) e.uniformId = 'Pilih jenis kaos';
    if (!qty || Number(qty) <= 0) e.qty = 'Jumlah harus lebih dari 0';
    if (!photoUrl) e.photo = 'Foto wajib diisi';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    try {
      await onSave({
        uniform_id: Number(uniformId),
        qty: Number(qty),
        url_photo: photoUrl || null,
        notes: notes.trim() || null,
      });
    } catch (err) {
      setErrors((p) => ({ ...p, submit: err?.message || 'Gagal menyimpan data' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleModalClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2>{editMode ? 'Edit Data' : 'Input Data'}</h2>
          <button className="modal__close" onClick={handleModalClose} disabled={saving || uploading}>✕</button>
        </div>

        <div className="modal__body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Jenis Kaos</label>
              <select
                value={uniformId}
                onChange={(e) => { setUniformId(e.target.value); setErrors((p) => ({ ...p, uniformId: undefined, submit: undefined })); }}
                className={errors.uniformId ? 'error' : ''}
                disabled={editMode}
              >
                <option value="">-- Pilih Jenis --</option>
                {uniforms.map((u) => (
                  <option key={u.uniform_id} value={u.uniform_id}>{u.name}</option>
                ))}
              </select>
              {errors.uniformId && <span className="error-msg">{errors.uniformId}</span>}
            </div>

            <div className="form-group">
              <label>Jumlah</label>
              <input
                type="number"
                min="1"
                value={qty}
                onChange={(e) => { setQty(e.target.value); setErrors((p) => ({ ...p, qty: undefined })); }}
                className={errors.qty ? 'error' : ''}
                placeholder="Masukkan jumlah"
              />
              {errors.qty && <span className="error-msg">{errors.qty}</span>}
            </div>

            {/* Photo picker — gaya galeri */}
            <div className="form-group">
              <label>Foto <span className="form-required">*</span></label>
              <div className="photo-picker__hint">Format gambar seperti JPG, JPEG, atau PNG didukung. Maks 1 MB.</div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                disabled={uploading || saving}
                onChange={handlePhotoChange}
              />
              {!photoPreview ? (
                <button
                  type="button"
                  className="photo-picker"
                  onClick={canRetryUpload ? handleRetryUpload : openFilePicker}
                  disabled={uploading}
                >
                  <span className="photo-picker__icon">📷</span>
                  <span className="photo-picker__label">{uploading ? 'Memproses foto...' : canRetryUpload ? 'Coba Lagi' : 'Tambah Foto'}</span>
                  <span className="photo-picker__sub">{uploading ? uploadStage : canRetryUpload ? 'Upload ulang foto terakhir' : 'Dari kamera atau galeri'}</span>
                </button>
              ) : (
                <div className="photo-picker__preview-wrap">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="photo-picker__preview"
                  />
                  <button
                    type="button"
                    className="photo-picker__remove"
                    onClick={handleRemovePhoto}
                    aria-label="Hapus foto"
                    disabled={uploading}
                  >
                    ✕
                  </button>
                  <button
                    type="button"
                    className="photo-picker__change"
                    onClick={canRetryUpload ? handleRetryUpload : openFilePicker}
                    disabled={uploading}
                  >
                    {uploading ? uploadStage : canRetryUpload ? 'Coba Lagi' : 'Ganti Foto'}
                  </button>
                </div>
              )}
              {uploading && <div className="photo-picker__status">{uploadStage}</div>}
              {uploadSuccess && !uploading && !uploadError && <div className="photo-picker__success">{uploadSuccess}</div>}
              {uploadError && <span className="error-msg">{uploadError}</span>}
              {errors.photo && !uploadError && <span className="error-msg">{errors.photo}</span>}
            </div>

            <div className="form-group">
              <label>Catatan <span className="form-optional">(opsional)</span></label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Tambahkan catatan..."
                rows={3}
              />
            </div>

            {errors.submit && (
              <div className="error-msg" style={{ marginBottom: 8 }}>{errors.submit}</div>
            )}
            <div className="modal__actions">
              <button type="button" className="btn btn-outline" onClick={handleModalClose} disabled={saving || uploading}>Batal</button>
              <button type="submit" className="btn btn-primary" disabled={saving || uploading}>
                {uploading ? 'Mengupload foto...' : saving ? 'Menyimpan...' : (editMode ? 'Simpan Perubahan' : 'Simpan')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InputModal;
